import { FunctionComponent } from "react"
import { Row, Col } from "antd"
import { ApolloError } from "@apollo/client"
import { DqiValue, METRIC_TYPES } from "src/pages/DataHealthDashboard/constants"
import { DataHealthCard } from "src/pages/DataHealthDashboard/components/DataHealthCards/DataHealthCard"
import { getDqiLabel } from "src/pages/DataHealthDashboard/utils/getDqiLabel"
import { theme } from "src/global/ui/themes/theme"
import { graphql } from "src/api/graphql/gql"
import { decimalToPercentageNumber } from "src/global/utils/formatNumber"
import { FragmentType, unmaskFragment } from "src/api/graphql/fragment-masking"
import { DataHealthCardsCoverageFragment as CoverageFragment } from "src/api/graphql/graphql"

export const DataHealthCardsMetricsFragment = graphql(`
  fragment DataHealthCardsMetrics on DataHealth {
    averageDailyScore
    averageDailyExpectationCount
  }
`)

export const DataHealthCardsCoverageFragment = graphql(`
  fragment DataHealthCardsCoverage on AggregateCoverageStats {
    activeAssets
    completenessCoverage
    numericCoverage
    schemaCoverage
    uniquenessCoverage
    validityCoverage
    volumeCoverage
  }
`)

export const DataHealthCardsDatasourcesFragment = graphql(`
  fragment DataHealthCardsDatasources on DatasourceV2 {
    id
    assets {
      id
    }
  }
`)

const getCoverageValue = (coverage: CoverageFragment | null | undefined, dqi: DqiValue): number => {
  if (!coverage) return 0

  const fieldMap = {
    overall: coverage.activeAssets,
    schema: coverage.schemaCoverage,
    volume: coverage.volumeCoverage,
    completeness: coverage.completenessCoverage,
    uniqueness: coverage.uniquenessCoverage,
    numeric: coverage.numericCoverage,
    validity: coverage.validityCoverage,
  } as const

  return fieldMap[dqi] ?? 0
}

const TITLE_CONFIGS = {
  [METRIC_TYPES.DATA_HEALTH]: {
    overallTitle: "Data Health",
    template: (dqiLabel: string) => `${dqiLabel} Health`,
  },
  [METRIC_TYPES.ACTIVE_COVERAGE]: {
    overallTitle: "Active Coverage",
    template: (dqiLabel: string) => `Active ${dqiLabel} Coverage`,
  },
  [METRIC_TYPES.FAILED_EXPECTATIONS]: {
    overallTitle: "Failed Expectations",
    template: (dqiLabel: string) => `Failed ${dqiLabel} Expectations`,
  },
} as const

const getDqiTitles = (dqi: DqiValue) => {
  const dqiLabel = getDqiLabel(dqi)

  if (dqiLabel === "Overall") {
    return {
      dataHealthTitle: TITLE_CONFIGS[METRIC_TYPES.DATA_HEALTH].overallTitle,
      activeCoverageTitle: TITLE_CONFIGS[METRIC_TYPES.ACTIVE_COVERAGE].overallTitle,
    }
  }

  return {
    dataHealthTitle: TITLE_CONFIGS[METRIC_TYPES.DATA_HEALTH].template(dqiLabel),
    activeCoverageTitle: TITLE_CONFIGS[METRIC_TYPES.ACTIVE_COVERAGE].template(dqiLabel),
  }
}

const getDqiTooltips = (dqi?: DqiValue) => {
  const dqiPrefix = dqi === "overall" ? "" : `${dqi} `

  return {
    dataHealthTooltip: `30-day average of the percentage of distinct ${dqiPrefix}Expectations that succeed in a day.`,
    activeCoverageTooltip: `Percentage of Data Assets that have been validated ${dqiPrefix ? `for ${dqiPrefix}` : ""}in the last 30 days.`,
  }
}

const getCoveragePercentage = (coverage?: CoverageFragment | null, dqi?: DqiValue): number => {
  const dqiKey = dqi || "overall"
  const coverageValue = getCoverageValue(coverage, dqiKey)
  return decimalToPercentageNumber(coverageValue, 0)
}

interface DataHealthCardsProps {
  dataHealth: FragmentType<typeof DataHealthCardsMetricsFragment> | null | undefined
  coverage: FragmentType<typeof DataHealthCardsCoverageFragment> | null | undefined
  datasources: FragmentType<typeof DataHealthCardsDatasourcesFragment>[] | null | undefined
  dataHealthLoading: boolean
  coverageLoading: boolean
  datasourcesLoading: boolean
  dataHealthError: ApolloError | undefined
  coverageError: ApolloError | undefined
  datasourcesError: ApolloError | undefined
  dqi: DqiValue
}

export const DataHealthCards: FunctionComponent<DataHealthCardsProps> = ({
  dataHealth,
  coverage,
  datasources,
  dataHealthLoading,
  coverageLoading,
  datasourcesLoading,
  dataHealthError,
  coverageError,
  datasourcesError,
  dqi,
}) => {
  const dataHealthMetrics = unmaskFragment(DataHealthCardsMetricsFragment, dataHealth)
  const coverageStats = unmaskFragment(DataHealthCardsCoverageFragment, coverage)
  const datasourcesList = datasources?.map((ds) => unmaskFragment(DataHealthCardsDatasourcesFragment, ds)) ?? []

  const totalAssets = datasourcesList.reduce((total: number, datasource) => {
    return total + (datasource.assets?.length || 0)
  }, 0)

  const { dataHealthTitle, activeCoverageTitle } = getDqiTitles(dqi)
  const { dataHealthTooltip, activeCoverageTooltip } = getDqiTooltips(dqi)

  const dataHealthPercentage = decimalToPercentageNumber(dataHealthMetrics?.averageDailyScore, 0)
  const expectationCount = dataHealthMetrics?.averageDailyExpectationCount ?? 0
  const dataHealthExpectationCount = Math.round(expectationCount)
  const activeCoveragePercentage = getCoveragePercentage(coverageStats, dqi)

  return (
    <Row
      gutter={[14, 14]}
      align="stretch"
      style={{
        paddingTop: theme.spacing.s,
        paddingBottom: theme.spacing.s,
      }}
    >
      <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{ minWidth: "320px" }}>
        <DataHealthCard
          title={dataHealthTitle}
          tooltip={dataHealthTooltip}
          percentage={dataHealthPercentage}
          progressLabel="Data Health success rate"
          metricLabel="Daily distinct Expectations"
          metricValue={dataHealthExpectationCount}
          metricIcon="fileSearchAlt"
          linkTo="data-health"
          loading={dataHealthLoading}
          error={dataHealthError}
        />
      </Col>
      <Col xs={24} sm={24} md={12} lg={8} xl={8} style={{ minWidth: "320px" }}>
        <DataHealthCard
          title={activeCoverageTitle}
          tooltip={activeCoverageTooltip}
          percentage={activeCoveragePercentage}
          progressLabel="Active coverage"
          metricLabel="Total Data Assets"
          metricValue={totalAssets}
          metricIcon="database"
          linkTo="active-coverage"
          loading={coverageLoading || datasourcesLoading}
          error={coverageError || datasourcesError}
        />
      </Col>
      <Col xs={24} sm={24} md={24} lg={8} xl={8} style={{ minWidth: "320px" }}>
        {/* TODO: Add failed expectations card using failedExpectationsTitle */}
      </Col>
    </Row>
  )
}
